import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import {
  WarningNotice,
  Panel,
  Input,
  H3,
  PanelHeader,
  PanelTitle,
  Label,
  Link,
  ExternalLink,
  Text,
} from '@studio/legacy-components';

import { selectRule, update } from 'next/entities/rules';

import { PriorityPanelBody, InputContainer, PreviewLabelText } from './styled';

import PrioritiesTable from './PrioritiesTable';

const LEARN_MORE_URL = 'https://docs.appcues.com/article/744-flow-priorities';

export const Prioritization = ({
  id,
  value = 0,
  max = 100000,
  min = -100000,
  onChange,
}) => {
  const sortPriority = useRef();
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    if (sortPriority.current) {
      sortPriority.current.value = value;
    }
  }, [value]);

  const handleBlur = ({ target: { valueAsNumber } }) => {
    if (
      Number.isNaN(valueAsNumber) ||
      valueAsNumber > max ||
      valueAsNumber < min
    ) {
      setErrorText(`Flow weight must be between ${min} and ${max}.`);
      sortPriority.current.value = value;
    } else if (valueAsNumber % 1 > 0) {
      const newValue = Number(valueAsNumber.toFixed(2));
      sortPriority.current.value = newValue;
      onChange(newValue);
    } else {
      const newValue = Math.round(valueAsNumber);
      onChange(newValue);
      sortPriority.current.value = newValue;
    }
  };

  const handleChange = () => void setErrorText('');

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>
          <H3>Flow priority</H3>
        </PanelTitle>
      </PanelHeader>
      <PriorityPanelBody>
        <Text secondary>
          If multiple Flows qualify to be shown to a user, the one with the
          greatest weight will be shown first.{' '}
          <Link to="/priorities">View Flow priority</Link>
        </Text>
        <InputContainer>
          <Label>
            Weight
            <Input
              placeholder="Enter number"
              type="number"
              name="sortPriority"
              aria-label="Sort priority"
              max={max}
              min={min}
              ref={sortPriority}
              defaultValue={value}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Label>
        </InputContainer>
        {errorText && <WarningNotice>{errorText}</WarningNotice>}
        <Label>
          <PreviewLabelText>
            Priority when published{' '}
            <ExternalLink href={LEARN_MORE_URL}>Learn more</ExternalLink>
          </PreviewLabelText>
          <PrioritiesTable id={id} />
        </Label>
      </PriorityPanelBody>
    </Panel>
  );
};

Prioritization.propTypes = {
  id: PropTypes.string,
  value: PropTypes.number,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
};

const mapStateToProps = (state, { id }) => {
  const { sortPriority = 0 } = selectRule(state, id);

  return {
    value: sortPriority,
  };
};

const mapDispatchToProps = (dispatch, { id }) => ({
  onChange: sortPriority => dispatch(update(id, { sortPriority })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Prioritization);
